import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { formatMoney, formatQty, MOVEMENT_KINDS, type CurrencyCode, type MovementKind } from '@tengesa/shared';
import { api } from '../../lib/api';
import type { Movement, ProductDto } from '../../lib/types';
import { useSession } from '../../app/session';
import { Button, Dialog, EmptyState, ErrorNotice, Spinner, formatDateTime } from '../../ui';

/** The stock ledger for one product at one branch: every movement and the running balance. */
export function StockHistoryDialog({ product, branchId, onClose }: { product: ProductDto; branchId: string; onClose: () => void }) {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const q = useInfiniteQuery({
    queryKey: ['movements', product.id, branchId],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) =>
      api.get<{ items: Movement[]; nextBefore: number | null }>(
        `/api/stock/movements?productId=${product.id}&branchId=${branchId}&limit=50${pageParam ? `&before=${pageParam}` : ''}`,
      ),
    getNextPageParam: (l) => l.nextBefore,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={`Stock history: ${product.name}`}
      description={product.stock ? `${formatQty(product.stock.qtyMilli)} in stock now${product.stock.avgCostCents != null ? `, average cost ${formatMoney(product.stock.avgCostCents, cur)}` : ''}` : undefined}
    >
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} />}
      {q.data && items.length === 0 && <EmptyState title="No stock movements yet" />}
      {items.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line text-muted">
              <th className="py-2 pr-3 font-medium">When</th>
              <th className="py-2 pr-3 font-medium">What</th>
              <th className="py-2 pr-3 text-right font-medium">Change</th>
              <th className="py-2 text-right font-medium">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {items.map((m) => (
              <tr key={m.id} className="align-top">
                <td className="py-2 pr-3 text-muted num">{formatDateTime(m.createdAt)}</td>
                <td className="py-2 pr-3">
                  {MOVEMENT_KINDS[m.kind as MovementKind] ?? m.kind} {m.refNumber && <span className="text-muted">{m.refNumber}</span>}
                  {(m.note || m.userName) && <span className="block text-xs text-muted">{[m.note, m.userName].filter(Boolean).join(' — ')}</span>}
                </td>
                <td className={clsx('py-2 pr-3 text-right num', m.qtyMilli < 0 ? 'text-danger' : 'text-ok')}>
                  {m.qtyMilli > 0 ? '+' : ''}
                  {formatQty(m.qtyMilli)}
                  {m.valueCents != null && <span className="block text-xs text-muted">{formatMoney(m.valueCents, cur)}</span>}
                </td>
                <td className="py-2 text-right font-medium num">{formatQty(m.qtyAfterMilli)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {q.hasNextPage && (
        <div className="mt-4 text-center">
          <Button size="sm" onClick={() => q.fetchNextPage()} busy={q.isFetchingNextPage}>Show older</Button>
        </div>
      )}
    </Dialog>
  );
}
